/**
 * LEGACY — Clinic Application Context (STATUS-CHECK ONLY)
 * =======================================================
 * STATUS: Legacy residual. All business logic has been cut over to the new
 * provider domain (`provider_domain_routes.py` + `AdminProviderDomain.jsx` +
 * `ProviderOnboarding.jsx`).
 *
 * This file retains ONLY the constants + a no-op provider/hook needed by the
 * 2 remaining legacy public magic-link status-check surfaces:
 *   - /clinic/application-status  (ApplicationStatusCheckPage.jsx)
 *   - /clinic/rejection-status    (RejectionStatusPage.jsx)
 *
 * Rules:
 *   - Do NOT add admin / onboarding / document-upload / CRUD logic here.
 *   - Do NOT use this context from any non-legacy file.
 *   - All state management for the public status-check flow must go through
 *     the magic-link API endpoints now living in provider_domain_routes.py
 *     (/api/provider/applications/request-access + /api/provider/applications/status).
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../utils/api';

const ClinicApplicationContext = createContext();

export const useClinicApplication = () => {
  const ctx = useContext(ClinicApplicationContext);
  if (!ctx) {
    throw new Error('useClinicApplication must be used within ClinicApplicationProvider');
  }
  return ctx;
};

// ── Status constants (kept for UI label mapping) ─────────────────────────────
export const APPLICATION_STATUS = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  PENDING: 'submitted',                    // legacy alias
  UNDER_REVIEW: 'under_review',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  INFO_REQUESTED: 'info_requested',
  MORE_INFO_REQUESTED: 'info_requested',   // legacy alias
  RESUBMITTED: 'resubmitted',
  REAPPLIED: 'resubmitted',                // legacy alias
  SUSPENDED: 'suspended',
};

// ── Flaggable field metadata (for rejection detail display) ──────────────────
export const FLAGGABLE_FIELDS = {
  clinicName:            { label: 'Clinic Name',                description: 'Business name mismatch' },
  email:                 { label: 'Email',                      description: 'Invalid or unverifiable email' },
  phone:                 { label: 'Phone Number',               description: 'Invalid phone number' },
  address:               { label: 'Address',                    description: 'Address could not be verified' },
  licenseNumber:         { label: 'License Number',             description: 'Invalid or expired license' },
  website:               { label: 'Website',                    description: 'Website not accessible' },
  businessLicense:       { label: 'Business License Document',  description: 'Missing or invalid document' },
  medicalCertifications: { label: 'Medical Certifications',     description: 'Required certifications missing' },
};

// Empty SPECIALIZATIONS array (kept for backwards-compatible import signature).
// Real specializations live in the treatment taxonomy API and are not needed
// by the legacy status-check surfaces.
export const SPECIALIZATIONS = [];

// ── Countries (used only by RejectionStatusPage display) ─────────────────────
export const COUNTRIES = [
  { code: 'TR', name: 'Turkey',                 flag: '' },
  { code: 'TH', name: 'Thailand',               flag: '' },
  { code: 'MX', name: 'Mexico',                 flag: '' },
  { code: 'IN', name: 'India',                  flag: '' },
  { code: 'MY', name: 'Malaysia',               flag: '' },
  { code: 'SG', name: 'Singapore',              flag: '' },
  { code: 'AE', name: 'United Arab Emirates',   flag: '' },
  { code: 'DE', name: 'Germany',                flag: '' },
  { code: 'ES', name: 'Spain',                  flag: '' },
  { code: 'KR', name: 'South Korea',            flag: '' },
  { code: 'BR', name: 'Brazil',                 flag: '' },
  { code: 'CO', name: 'Colombia',               flag: '' },
  { code: 'PL', name: 'Poland',                 flag: '' },
  { code: 'HU', name: 'Hungary',                flag: '' },
  { code: 'CZ', name: 'Czech Republic',         flag: '' },
  { code: 'GB', name: 'United Kingdom',         flag: '' },
  { code: 'OTHER', name: 'Other',               flag: '' },
];

// Kept for backwards-compat — unused by the new domain.
export const FLAGGABLE_FIELD_KEYS = Object.keys(FLAGGABLE_FIELDS);

// ── No-op provider + minimal hook (status-check legacy only) ────────────────
export const ClinicApplicationProvider = ({ children }) => {
  const [applications, setApplications] = useState([]);

  // Legacy stub — only the public magic-link status-check page uses this,
  // and it does so by searching a list populated via setApplications after
  // a successful /provider/applications/status call.
  const getApplicationById = useCallback((id) => {
    return applications.find(a => a.id === id || a.application_id === id) || null;
  }, [applications]);

  // Legacy stub — reapply goes through the new domain's resubmit endpoint.
  const reapplyApplication = useCallback(async (applicationId, payload) => {
    try {
      const res = await api.post(
        `/provider/applications/${applicationId}/resubmit`,
        payload || {}
      );
      return res.data;
    } catch (err) {
      console.error('[LegacyStatusCheck] resubmit failed:', err);
      throw err;
    }
  }, []);

  const value = {
    // data
    applications,
    setApplications,
    // stubs preserved for backwards-compat with the 2 legacy status-check pages
    getApplicationById,
    reapplyApplication,
    SPECIALIZATIONS,
  };

  return (
    <ClinicApplicationContext.Provider value={value}>
      {children}
    </ClinicApplicationContext.Provider>
  );
};

export default ClinicApplicationContext;
